import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, CalendarDays, AlertCircle, Loader2 } from "lucide-react";
import { format, addDays, nextMonday } from "date-fns";
import { vi } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RadioOption = ({ id, value, label, desc, name }) => (
  <div className="flex items-start space-x-3 py-2">
    <RadioGroupItem value={value} id={id} className="mt-0.5" />
    <Label htmlFor={id} className="cursor-pointer">
      <div className="font-medium text-sm">{label}</div>
      {desc && <div className="text-slate-500 text-xs mt-0.5">{desc}</div>}
    </Label>
  </div>
);

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const wizardData = location.state || {};
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    age_group: "35to45",
    gender: "male",
    health: "healthy",
    training_history: "1to5yr",
    swim_level: "B1",
    bike_level: "D1",
    run_level: "C2",
    has_smart_trainer: true,
    has_powermeter: false,
    threshold_pace: "",
    threshold_hr: "",
    ftp: "",
    start_date: nextMonday(new Date()),
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const dist = wizardData.distance || "70.3";
      const payload = {
          training_plan_name: dist === "70.3" ? "Half Ironman 70.3" : "Full Ironman 140.6",
          sport_type: `triathlon_${dist}`,
          age: form.age_group === "under35" ? 30 : form.age_group === "35to45" ? 40 : 50,
          gender: form.gender,
          training_level: wizardData.level || "finisher",
          training_duration: wizardData.duration || 16,
          training_intensity: wizardData.intensity || "medium",
          weaker_sport: form.swim_level === "B0" ? "swim" : form.bike_level === "D0" ? "bike" : form.run_level === "C0" ? "run" : null,
          running_threshold_pace: form.threshold_pace || null,
          maximum_heart_rate: form.threshold_hr ? parseInt(form.threshold_hr) : null,
          cycling_ftp: form.ftp ? parseInt(form.ftp) : null,
        };

      const res = await axios.post(`${API}/plans/generate`, payload);
      if (res.data.plan_id) {
        navigate(`/plan/${res.data.plan_id}`);
      }
    } catch (err) {
      toast.error("Có lỗi xảy ra. Vui lòng thử lại.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const warnings = [];
  if (form.swim_level === "B0") warnings.push({ type: "error", msg: "Bạn chưa biết bơi. Nên dành 2 tháng tập bơi trước khi vào plan." });
  if (form.bike_level === "D0") warnings.push({ type: "warning", msg: "Bạn chưa đạp xe bao giờ. Nên dành 1 tháng tập đạp xe ngoài đường trước." });
  if (form.health === "injured_now") warnings.push({ type: "error", msg: "Bạn đang chấn thương. Plan sẽ giảm volume 30% và thêm lưu ý an toàn." });
  if (form.health === "injured_3mo") warnings.push({ type: "warning", msg: "Chấn thương trong 3 tháng gần đây. Plan sẽ giảm volume 20%." });

  return (
    <div className="min-h-screen track-texture">
      <header className="bg-slate-900 text-white py-5 px-4 no-print">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-white hover:text-orange-400" data-testid="back-to-wizard-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-black uppercase tracking-tight">
              <span className="text-orange-400">Cá nhân hoá</span> Plan
            </h1>
            {wizardData.planCode && (
              <Badge className="bg-orange-500 text-white text-xs mt-1">{wizardData.planCode} - {wizardData.distance} - {wizardData.duration} tuần</Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="space-y-2">
            {warnings.map((w, i) => (
              <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                w.type === "error" ? "bg-red-50 border border-red-200 text-red-700" : "bg-amber-50 border border-amber-200 text-amber-700"
              }`} data-testid={`warning-${i}`}>
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{w.msg}</span>
              </div>
            ))}
          </div>
        )}

        {/* Personal Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg uppercase tracking-tight">Thông tin cá nhân</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-2 block">Nhóm tuổi</Label>
              <RadioGroup value={form.age_group} onValueChange={(v) => update("age_group", v)} data-testid="age-group-radio">
                <RadioOption id="age-u35" value="under35" label="Dưới 35" />
                <RadioOption id="age-35" value="35to45" label="Từ 35 đến 45" />
                <RadioOption id="age-o45" value="over45" label="Trên 45" desc="Sẽ thêm ngày recovery và giảm intensity bài AO/VO" />
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-2 block">Giới tính</Label>
              <RadioGroup value={form.gender} onValueChange={(v) => update("gender", v)} className="flex gap-6" data-testid="gender-radio">
                <RadioOption id="gender-m" value="male" label="Nam" />
                <RadioOption id="gender-f" value="female" label="Nữ" />
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-2 block">Sức khoẻ</Label>
              <RadioGroup value={form.health} onValueChange={(v) => update("health", v)} data-testid="health-radio">
                <RadioOption id="h-now" value="injured_now" label="Đang chấn thương" />
                <RadioOption id="h-3mo" value="injured_3mo" label="Chấn thương trong 3 tháng" />
                <RadioOption id="h-6mo" value="injured_6mo" label="Chấn thương trong 6 tháng" />
                <RadioOption id="h-ok" value="healthy" label="Khoẻ mạnh" />
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-2 block">Lịch sử tập luyện</Label>
              <RadioGroup value={form.training_history} onValueChange={(v) => update("training_history", v)} data-testid="history-radio">
                <RadioOption id="hist-1" value="under1yr" label="Dưới 1 năm" />
                <RadioOption id="hist-5" value="1to5yr" label="Từ 1 đến 5 năm" />
                <RadioOption id="hist-5p" value="over5yr" label="Trên 5 năm" />
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Abilities */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg uppercase tracking-tight">Năng lực hiện tại</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-sky-600 mb-2 block">Bơi lội</Label>
              <RadioGroup value={form.swim_level} onValueChange={(v) => update("swim_level", v)} data-testid="swim-level-radio">
                <RadioOption id="s-b0" value="B0" label="Chưa biết bơi" desc="Nên học bơi trước khi bắt đầu plan" />
                <RadioOption id="s-b1" value="B1" label="Chưa bơi quá 1000m" />
                <RadioOption id="s-b2" value="B2" label="Có thể bơi từ 1000m trở lên" />
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold text-emerald-600 mb-2 block">Đạp xe</Label>
              <RadioGroup value={form.bike_level} onValueChange={(v) => update("bike_level", v)} data-testid="bike-level-radio">
                <RadioOption id="b-d0" value="D0" label="Chưa đạp xe bao giờ" desc="Nên tập 1 tháng trước" />
                <RadioOption id="b-d1" value="D1" label="Chưa đạp quá 40km" />
                <RadioOption id="b-d2" value="D2" label="Đạp trên 40km 1-2 lần/tuần" />
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold text-red-600 mb-2 block">Chạy bộ</Label>
              <RadioGroup value={form.run_level} onValueChange={(v) => update("run_level", v)} data-testid="run-level-radio">
                <RadioOption id="r-c0" value="C0" label="Chưa chạy quá 10km" />
                <RadioOption id="r-c1" value="C1" label="10km trên 1h15p" />
                <RadioOption id="r-c2" value="C2" label="10km từ 50p đến 1h15p" />
                <RadioOption id="r-c3" value="C3" label="10km dưới 50 phút" />
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Equipment */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg uppercase tracking-tight">Thiết bị</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-2 block">Smart trainer</Label>
              <RadioGroup
                value={form.has_smart_trainer === true ? "yes" : form.has_smart_trainer === false ? "no" : "unknown"}
                onValueChange={(v) => update("has_smart_trainer", v === "yes" ? true : v === "no" ? false : null)}
                className="flex gap-4 flex-wrap"
                data-testid="smart-trainer-radio"
              >
                <RadioOption id="st-y" value="yes" label="Có" />
                <RadioOption id="st-n" value="no" label="Không" />
                <RadioOption id="st-u" value="unknown" label="Chưa biết" />
              </RadioGroup>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-600 mb-2 block">Powermeter</Label>
              <RadioGroup
                value={form.has_powermeter === true ? "yes" : form.has_powermeter === false ? "no" : "unknown"}
                onValueChange={(v) => update("has_powermeter", v === "yes" ? true : v === "no" ? false : null)}
                className="flex gap-4 flex-wrap"
                data-testid="powermeter-radio"
              >
                <RadioOption id="pm-y" value="yes" label="Có" />
                <RadioOption id="pm-n" value="no" label="Không" />
                <RadioOption id="pm-u" value="unknown" label="Chưa biết" />
              </RadioGroup>
            </div>
          </CardContent>
        </Card>

        {/* Thresholds */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg uppercase tracking-tight">Chỉ số ngưỡng (tuỳ chọn)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-500 text-xs">Nếu chưa biết, có thể bỏ qua. Hệ thống sẽ sử dụng giá trị mặc định.</p>
            <div>
              <Label htmlFor="tp" className="text-sm font-medium">Running Threshold Pace (mm:ss/km)</Label>
              <Input
                id="tp"
                placeholder="VD: 5:30"
                value={form.threshold_pace}
                onChange={(e) => update("threshold_pace", e.target.value)}
                className="mt-1"
                data-testid="threshold-pace-input"
              />
            </div>
            <div>
              <Label htmlFor="thr" className="text-sm font-medium">Threshold HR (bpm)</Label>
              <Input
                id="thr"
                type="number"
                placeholder="VD: 170"
                value={form.threshold_hr}
                onChange={(e) => update("threshold_hr", e.target.value)}
                className="mt-1"
                data-testid="threshold-hr-input"
              />
            </div>
            <div>
              <Label htmlFor="ftp" className="text-sm font-medium">FTP (watts)</Label>
              <Input
                id="ftp"
                type="number"
                placeholder="VD: 200"
                value={form.ftp}
                onChange={(e) => update("ftp", e.target.value)}
                className="mt-1"
                data-testid="ftp-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Start Date */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg uppercase tracking-tight">Ngày bắt đầu plan</CardTitle>
          </CardHeader>
          <CardContent>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-medium" data-testid="start-date-btn">
                  <CalendarDays className="w-4 h-4 mr-2 text-orange-500" />
                  {form.start_date ? format(form.start_date, "EEEE, dd/MM/yyyy", { locale: vi }) : "Chọn ngày"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={form.start_date}
                  onSelect={(d) => d && update("start_date", d)}
                  disabled={(date) => date < new Date()}
                  data-testid="start-date-calendar"
                />
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {/* Submit */}
        <Button
          className="btn-skew bg-orange-500 hover:bg-orange-600 text-white w-full py-6 text-lg"
          onClick={handleSubmit}
          disabled={loading}
          data-testid="generate-plan-btn"
        >
          <span className="flex items-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {loading ? "Đang tạo plan..." : "Tạo Training Plan"}
          </span>
        </Button>

        <div className="h-8" />
      </main>
    </div>
  );
}
